import React, { createContext, useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';

export const WebSocketContext = createContext(null);

// Función auxiliar para normalizar URLs y sus parámetros
const normalizeUrl = (baseUrl, params = {}) => {
  // Asegúrate de que la URL base esté limpia
  const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
  const url = new URL(cleanBaseUrl);
  
  // Añadir cada parámetro con doble encodeURIComponent para casos extremos
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== null) {
      // Primero normaliza el valor eliminando caracteres problemáticos
      const normalizedValue = String(params[key])
        .trim()
        .replace(/[\s+]/g, ' ');
      
      // Luego codifica de forma segura
      url.searchParams.append(key, encodeURIComponent(normalizedValue));
    }
  });
  
  return url.toString();
};

// Función auxiliar para sanitizar datos recibidos
const sanitizeIncomingData = (data) => {
  if (!data || typeof data !== 'object') {
    return data;
  }
  
  // Clonar el objeto para no modificar el original
  const sanitized = { ...data };
  
  // Sanitizar campos comunes
  if (sanitized.user_id) {
    sanitized.user_id = String(sanitized.user_id).trim();
  }
  
  if (sanitized.username) {
    sanitized.username = String(sanitized.username).trim();
  }
  
  // Si hay un mensaje anidado, sanitizarlo también
  if (sanitized.message && typeof sanitized.message === 'object') {
    sanitized.message = sanitizeIncomingData(sanitized.message);
  }
  
  return sanitized;
};

export const WebSocketProvider = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const socket = useRef(null);
  const messageHandlers = useRef([]);
  const { user, isAuthenticated, refreshToken } = useAuth();
  const reconnectTimeout = useRef(null);
  const reconnectAttempts = useRef(0);
  const isConnecting = useRef(false);
  // Usar un Set para mensajes procesados (más simple y eficiente)
  const processedMessageIds = useRef(new Set());
  // Definir connectRef para almacenar la función connect
  const connectRef = useRef(null);
  
  // Limpiar mensajes procesados cada 5 minutos para evitar fugas de memoria
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      // Simplemente limpiar el conjunto cada cierto tiempo
      processedMessageIds.current.clear();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(cleanupInterval);
  }, []);
  
  // Mover la definición de attemptReconnect antes del useEffect que la usa
  const attemptReconnect = useCallback(() => {
    if (!connectRef.current || !isAuthenticated || isConnecting.current || 
        (socket.current && socket.current.readyState === WebSocket.OPEN)) {
      return;
    }

    const backoff = Math.min(60, Math.pow(2, reconnectAttempts.current)) * 1000;

    reconnectTimeout.current = setTimeout(async () => {
      if (isAuthenticated) {
        reconnectAttempts.current += 1;

        try {
          const isValid = await refreshToken();
          if (isValid && !isConnecting.current) {
            connectRef.current();
          }
        } catch (err) {
          console.error("Error en reconexión:", err);
        }
      }
    }, backoff);
  }, [isAuthenticated, refreshToken]);

  // Función para enviar mensaje de autenticación
  const sendAuthMessage = useCallback((user) => {
    if (!socket.current || socket.current.readyState !== WebSocket.OPEN || !user) return;
    
    // Usar authToken en lugar de token para ser consistente
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      return;
    }
    
    // Sanitizar datos antes de enviarlos
    const sanitizedUserId = user.id ? String(user.id).trim() : '';
    const sanitizedUsername = user.username ? String(user.username).trim() : '';
    
    const authMessage = {
      type: 'auth',
      user_id: sanitizedUserId,
      username: sanitizedUsername,
      token: token
    };
    
    try {
      socket.current.send(JSON.stringify(authMessage));
    } catch (error) {
      // Error handling silently
    }
  }, []);

  // Procesar mensaje recibido por WebSocket
  const processWebSocketMessage = useCallback((data) => {
    // Verificar si es un ping o un mensaje vacío
    if (data.type === 'ping' || !data) return;
    
    // Sanitizar los datos recibidos
    const sanitizedData = sanitizeIncomingData(data);
    
    // Generar un ID único para este mensaje
    const msgIdParts = [
      sanitizedData.type || '',
      sanitizedData.id || sanitizedData.message?.id || '',
      sanitizedData.timestamp || sanitizedData.message?.timestamp || Date.now()
    ];
    const msgId = msgIdParts.join('-');
    
    // Verificar si ya procesamos este mensaje
    if (processedMessageIds.current.has(msgId)) {
      return; // Ya procesado, evita duplicados
    }
    
    // Registrar este mensaje como procesado
    processedMessageIds.current.add(msgId);
    
    // Notificar a los handlers registrados
    messageHandlers.current.forEach(handler => {
      try {
        handler(sanitizedData);
      } catch (error) {
        // Error handling silently
      }
    });
  }, []);

  // Store stable references to user and isAuthenticated to prevent unnecessary reconnects
  const stableUserRef = useRef(user);
  const stableAuthRef = useRef(isAuthenticated);
  
  // Only update the refs when the values actually change in a meaningful way
  useEffect(() => {
    if (isAuthenticated !== stableAuthRef.current) {
      stableAuthRef.current = isAuthenticated;
    }
    if (user && stableUserRef.current && user.id !== stableUserRef.current.id) {
      stableUserRef.current = user;
    } else if (!stableUserRef.current && user) {
      stableUserRef.current = user;
    } else if (stableUserRef.current && !user) {
      stableUserRef.current = null;
    }
  }, [isAuthenticated, user]);

  // Efecto principal para gestionar la conexión WebSocket
  useEffect(() => {
    const isMounted = { current: true };
    let connectionInProgress = false;
    
    // Añadir esto al inicio del useEffect
    window.addEventListener('beforeunload', () => {
      // Marcar como desmontado inmediatamente antes de la navegación
      isMounted.current = false;
      
      // Cerrar el socket inmediatamente para evitar reconexiones al navegar
      if (socket.current) {
        socket.current.close();
      }
    });
    
    // Definir la función connect
    const connect = async () => {
      // Prevenir conexiones múltiples con bloqueo estricto
      if (connectionInProgress || 
          isConnecting.current || 
          socket.current?.readyState === WebSocket.CONNECTING || 
          socket.current?.readyState === WebSocket.OPEN) {
        return;
      }

      connectionInProgress = true;
      isConnecting.current = true;

      // Verificar token y usuario
      const token = localStorage.getItem('authToken');
      if (!token || !stableUserRef.current || !stableUserRef.current.id) {
        setConnected(false);
        connectionInProgress = false;
        isConnecting.current = false;
        return;
      }

      try {
        // Validar token antes de intentar conectar
        const isValidToken = await refreshToken();
        if (!isValidToken || !isMounted.current) {
          throw new Error('Token inválido o componente desmontado');
        }

        // Cerrar socket existente si hay uno
        if (socket.current) {
          socket.current.onclose = null;
          socket.current.onerror = null;
          socket.current.onmessage = null;
          socket.current.onopen = null;
          socket.current.close();
          socket.current = null;
        }

        // Si el componente se desmontó durante la validación, salir
        if (!isMounted.current) {
          connectionInProgress = false;
          isConnecting.current = false;
          return;
        }

        // Construir URL con parámetros sanitizados
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = process.env.NODE_ENV === 'development' ? 'localhost:8080' : window.location.host;
        const baseUrl = `${protocol}//${host}/ws`;
        
        const wsUrl = normalizeUrl(baseUrl, {
          user_id: stableUserRef.current.id,
          token // Añadir el token como parámetro
        });

        // Crear nuevo socket con timeout
        socket.current = new WebSocket(wsUrl);
        
        // Configurar timeout más largo para la conexión inicial
        const connectionTimeout = setTimeout(() => {
          if (socket.current && socket.current.readyState !== WebSocket.OPEN) {
            socket.current.close();
            if (isMounted.current) {
              setConnected(false);
            }
          }
          connectionInProgress = false;
          isConnecting.current = false;
        }, 30000); // 30 segundos de timeout

        socket.current.onopen = () => {
          clearTimeout(connectionTimeout);
          reconnectAttempts.current = 0;
          
          if (isMounted.current) {
            setConnected(true);
          }
          
          // Enviar autenticación inmediatamente
          if (stableAuthRef.current && stableUserRef.current && socket.current) {
            sendAuthMessage(stableUserRef.current);
          }
          
          connectionInProgress = false;
          isConnecting.current = false;
        };

        // Mejorar el manejo de errores
        socket.current.onerror = (error) => {
          console.error("WebSocket error:", error);
          clearTimeout(connectionTimeout);
          if (socket.current) {
            socket.current.close();
          }
          connectionInProgress = false;
          isConnecting.current = false;
        };

        socket.current.onclose = (event) => {
          clearTimeout(connectionTimeout);
          if (isMounted.current) {
            setConnected(false);
          }
          
          connectionInProgress = false;
          isConnecting.current = false;

          // Solo intentar reconexión si:
          // 1. El usuario está autenticado
          // 2. No fue un cierre limpio
          // 3. El componente sigue montado
          if (stableAuthRef.current && !event.wasClean && isMounted.current) {
            attemptReconnect();
          }
        };

        socket.current.onmessage = (event) => {
          if (!isMounted.current) return;
          
          try {
            const data = JSON.parse(event.data);
            
            // Usar la función para procesar mensajes
            processWebSocketMessage(data);
          } catch (error) {
            // Error handling silently
          }
        };
      } catch (error) {
        console.error("Error en la conexión WebSocket:", error);
        connectionInProgress = false;
        isConnecting.current = false;
        if (isMounted.current) {
          setConnected(false);
        }
      }
    };
    
    // Guardar la referencia a connect
    connectRef.current = connect;
    
    // Solo conectar si el usuario está autenticado, con un breve retraso para evitar reconexiones rápidas
    let connectTimeout;
    if (stableAuthRef.current && stableUserRef.current) {
      connectTimeout = setTimeout(connect, 300);
    } else {
      // Si hay un socket abierto y el usuario no está autenticado, cerrarlo
      if (socket.current) {
        socket.current.close();
      }
    }
    
    return () => {
      // Marcar como desmontado
      isMounted.current = false;
      
      // Limpiar timeout de conexión
      if (connectTimeout) {
        clearTimeout(connectTimeout);
      }
      
      window.removeEventListener('beforeunload', () => {
        isMounted.current = false;
      });
      
      // Limpiar timeout de reconexión
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
      
      // Cerrar y limpiar el socket
      if (socket.current) {
        // Eliminar todos los handlers para evitar ejecuciones asíncronas después de desmontar
        socket.current.onclose = null;
        socket.current.onerror = null;
        socket.current.onmessage = null;
        socket.current.onopen = null;
        
        // Cerrar el socket
        socket.current.close();
        socket.current = null;
      }
      
      // Reiniciar estado de conexión
      connectionInProgress = false;
      isConnecting.current = false;
    };
  // Use the ref values instead of the actual values to prevent reconnects on theme changes
  }, [sendAuthMessage, processWebSocketMessage, refreshToken, attemptReconnect]);
  
  // Función para enviar mensajes
  const sendMessage = useCallback((message) => {
    if (!socket.current || socket.current.readyState !== WebSocket.OPEN) {
      return false;
    }
    
    try {
      // Validar que el mensaje es un objeto válido
      if (typeof message !== 'object' || message === null) {
        console.error("Mensaje inválido: debe ser un objeto");
        return false;
      }
      
      // Si el mensaje contiene datos sensibles como IDs, asegurarnos de que estén sanitizados
      const sanitizedMessage = { ...message };
      if (sanitizedMessage.user_id) {
        sanitizedMessage.user_id = String(sanitizedMessage.user_id).trim();
      }
      if (sanitizedMessage.username) {
        sanitizedMessage.username = String(sanitizedMessage.username).trim();
      }
      
      socket.current.send(JSON.stringify(sanitizedMessage));
      return true;
    } catch (error) {
      console.error("Error al enviar mensaje:", error);
      return false;
    }
  }, []);
  
  // Registrar un handler para recibir mensajes
  const addMessageHandler = useCallback((handler) => {
    if (typeof handler !== 'function') return;
    
    // Evitar duplicados
    if (!messageHandlers.current.includes(handler)) {
      messageHandlers.current.push(handler);
    }
    
    // Función para eliminar el handler
    return () => {
      messageHandlers.current = messageHandlers.current.filter(h => h !== handler);
    };
  }, []);
  
  return (
    <WebSocketContext.Provider value={{ connected, sendMessage, addMessageHandler }}>
      {children}
    </WebSocketContext.Provider>
  );
};